import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Music, Tag, Link as LinkIcon, Lock, ExternalLink } from 'lucide-react';
import VenueMap from '../components/VenueMap';
import Seo from '../components/Seo';

const VenuesPage = () => {
  const [selectedCity, setSelectedCity] = useState('london');
  const [selectedSubgenre, setSelectedSubgenre] = useState(null);

  const venues = {
    london: [
      { name: "E1", address: "Unit 2, 110 Pennington St, London E1W 2BB", coords: { lat: 51.5082, lng: -0.0678 } },
      { name: "The Egg", address: "5-13 Vale Royal, London N7 9AP", coords: { lat: 51.5527, lng: -0.1210 } },
      { name: "Venue MOT Unit 18", address: "Orion Business Centre, Surrey Canal Rd, London SE14 5RT", coords: { lat: 51.4843, lng: -0.0432 } },
      { name: "HWK London", address: "29 White Post Ln, London E9 5EN", coords: { lat: 51.5437, lng: -0.0258 } },
      { name: "Fold", address: "Gillian House, Stephenson St, London E16 4SA", coords: { lat: 51.5060, lng: 0.0150 } },
      { name: "Fabric", address: "77A Charterhouse St, London EC1M 6HJ", coords: { lat: 51.5195, lng: -0.1025 } },
      { name: "The Cause", address: "60 Dock Rd, London E16 1YZ", coords: { lat: 51.5044, lng: 0.0194 } },
      { name: "Planet Wax", address: "318 New Cross Rd, London SE14 6AF", coords: { lat: 51.4754, lng: -0.0421 } },
    ],
    pune: [
      // `link` is optional — when set, the venue card becomes a <Link> so
      // users can jump from a venue directly to relevant events/pages.
      { name: "Vault", address: "Senapati Bapat Rd, Laxmi Society, Model Colony, Shivajinagar, Pune 411016", coords: { lat: 18.5292, lng: 73.8337 }, link: "/events" },
      { name: "Antisocial", address: "Unit 101, Cello Platina, Fergusson College Rd, Shivajinagar, Pune 411005", coords: { lat: 18.5254, lng: 73.8410 } },
      { name: "BLAK", address: "Marriott Suites Pune, 81 Mundhwa Rd, Koregaon Park Annexe, Pune 411036", coords: { lat: 18.5378, lng: 73.8985 } },
      { name: "Stone Club", address: "Koregaon Park, Pune", coords: { lat: 18.5365, lng: 73.8945 } },
    ]
  };

  const cityCenters = {
    london: { center: [51.5138, -0.0670], zoom: 11 },
    pune: { center: [18.5320, 73.8650], zoom: 13 },
  };

  const genres = [
    {
      name: "Grassroot",
      subgenres: [
        { name: "Hard Techno", description: "Aggressive, high-energy techno with distorted kicks" },
        { name: "Industrial Techno", description: "Dark, mechanical sounds with heavy percussion" },
        { name: "Melodic Techno", description: "Atmospheric with emotional melodies and progressive builds" },
        { name: "Minimal Techno", description: "Stripped-back, hypnotic grooves with subtle variations" },
        { name: "Acid Techno", description: "Characterized by the squelchy 303 bassline" },
        { name: "Hypnotic / Deep Techno", description: "Trance-inducing, deep and layered soundscapes" },
        { name: "Peak-Time Techno", description: "High-energy tracks for the peak hours of the night" },
      ]
    }
  ];

  const tags = {
    vibe: [
      { name: "Dark", color: "bg-zinc-800" },
      { name: "Melodic", color: "bg-blue-900/50" },
      { name: "Atmospheric", color: "bg-purple-900/50" },
      { name: "Aggressive", color: "bg-red-900/50" },
    ],
    crowd: [
      { name: "Commercial", color: "bg-yellow-900/50" },
      { name: "Underground", color: "bg-zinc-700" },
      { name: "Festival", color: "bg-orange-900/50" },
      { name: "Afterparty", color: "bg-indigo-900/50" },
    ],
    cultural: [
      { name: "Bollywood", color: "bg-pink-900/50" },
      { name: "DesiFusion", color: "bg-amber-900/50" },
    ],
    energy: [
      { name: "HighEnergy", color: "bg-red-800/50" },
      { name: "FastBPM", color: "bg-orange-800/50" },
      { name: "Groovy", color: "bg-green-900/50" },
    ]
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="venues-page">
      <Seo title="Nightlife Venues Map" description="Discover the clubs, warehouses, and hidden underground venues hosting Unight events. Interactive map of every location we play across the UK and India." path="/venues" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 to-transparent" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <p className="text-zinc-500 text-lg italic mb-4 opacity-0 animate-fade-in" 
             style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            Discover
          </p>
          
          <h1 
            className="font-['Josefin_Sans'] text-5xl md:text-6xl lg:text-7xl font-bold italic text-white mb-6 opacity-0 animate-fade-in-up tracking-tight"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards', letterSpacing: '-0.059em' }}
            data-testid="venues-title"
          >
            GRASSROOT VENUES
          </h1>
          
          <p className="text-zinc-400 max-w-2xl opacity-0 animate-fade-in-up"
             style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            Explore the best underground grassroot venues across the UK and India. From legendary London warehouses to Pune's vibrant club scene.
          </p>
        </div>
      </section>

      {/* City Selector */}
      <section className="py-8 border-b border-zinc-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex gap-4">
            <button
              onClick={() => setSelectedCity('london')}
              className={`px-6 py-3 font-mono text-sm tracking-widest transition-all ${
                selectedCity === 'london'
                  ? 'bg-[#B5FF00] text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              LONDON
            </button>
            <button
              onClick={() => setSelectedCity('pune')}
              className={`px-6 py-3 font-mono text-sm tracking-widest transition-all ${
                selectedCity === 'pune'
                  ? 'bg-[#B5FF00] text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              PUNE
            </button>
          </div>
        </div>
      </section>

      {/* Interactive Venue Map */}
      <section className="py-16" data-testid="venue-map-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-8">
            <MapPin className="text-[#B5FF00]" size={24} />
            <h2 className="font-['Josefin_Sans'] text-3xl font-semibold text-white tracking-tight" style={{ letterSpacing: '-0.059em' }}>
              {selectedCity === 'london' ? 'LONDON' : 'PUNE'} VENUES
            </h2>
          </div>

          {/* Map Container */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Real Leaflet + OpenStreetMap */}
            <VenueMap
              venues={venues[selectedCity]}
              center={cityCenters[selectedCity].center}
              zoom={cityCenters[selectedCity].zoom}
              cityKey={selectedCity}
            />

            {/* Venue List */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {venues[selectedCity].map((venue) => {
                const cardBody = (
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-['Josefin_Sans'] text-lg text-white group-hover:text-[#B5FF00] transition-colors">
                        {venue.name}
                      </h3>
                      <p className="text-zinc-500 text-sm mt-1">{venue.address}</p>
                      {venue.link && (
                        <p className="text-[#B5FF00] text-xs font-mono tracking-widest mt-2 opacity-80 group-hover:opacity-100">
                          VIEW EVENTS →
                        </p>
                      )}
                    </div>
                    <ExternalLink size={18} className="text-zinc-600 group-hover:text-[#B5FF00] transition-colors flex-shrink-0" />
                  </div>
                );
                const cardClass = "block bg-[#0A0A0A] border border-zinc-800 p-4 hover:border-[#B5FF00]/50 transition-all cursor-pointer group";
                if (venue.link) {
                  return (
                    <Link
                      key={venue.name}
                      to={venue.link}
                      className={cardClass}
                      data-testid={`venue-card-${venue.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {cardBody}
                    </Link>
                  );
                }
                return (
                  <div
                    key={venue.name}
                    className={cardClass}
                    data-testid={`venue-card-${venue.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cardBody}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sub-genre Directory */}
      <section className="py-16 border-t border-zinc-800 bg-[#0A0A0A]" data-testid="subgenre-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Music className="text-[#B5FF00]" size={24} />
            <h2 className="font-['Josefin_Sans'] text-3xl font-semibold text-white tracking-tight" style={{ letterSpacing: '-0.059em' }}>
              GENRE DIRECTORY
            </h2>
          </div>

          <p className="text-zinc-400 mb-8 max-w-2xl">
            Navigate through grassroot subgenres and find events that match your vibe. From hard-hitting industrial to ethereal melodic sounds.
          </p>

          {/* Genre Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {genres[0].subgenres.map((subgenre) => (
              <button
                key={subgenre.name}
                onClick={() => setSelectedSubgenre(selectedSubgenre === subgenre.name ? null : subgenre.name)}
                className={`p-5 border text-left transition-all ${
                  selectedSubgenre === subgenre.name
                    ? 'bg-[#B5FF00]/10 border-[#B5FF00]/50'
                    : 'bg-[#050505] border-zinc-800 hover:border-zinc-700'
                }`}
              >
                <h3 className="font-['Josefin_Sans'] text-lg text-white mb-2">{subgenre.name}</h3>
                <p className="text-zinc-500 text-sm">{subgenre.description}</p>
              </button>
            ))}
          </div>

          {/* Tags Section */}
          <div className="border-t border-zinc-800 pt-8">
            <div className="flex items-center gap-3 mb-6">
              <Tag className="text-[#B5FF00]" size={20} />
              <h3 className="font-['Josefin_Sans'] text-xl font-semibold text-white">FILTER BY TAGS</h3>
            </div>

            <div className="space-y-6">
              {/* Vibe Tags */}
              <div>
                <p className="font-mono text-xs text-zinc-500 tracking-widest mb-3">VIBE</p>
                <div className="flex flex-wrap gap-2">
                  {tags.vibe.map((tag) => (
                    <span key={tag.name} className={`${tag.color} px-3 py-1.5 text-sm text-white border border-zinc-700 hover:border-[#B5FF00]/50 cursor-pointer transition-all`}>
                      #{tag.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Crowd Tags */}
              <div>
                <p className="font-mono text-xs text-zinc-500 tracking-widest mb-3">CROWD / ACCESSIBILITY</p>
                <div className="flex flex-wrap gap-2">
                  {tags.crowd.map((tag) => (
                    <span key={tag.name} className={`${tag.color} px-3 py-1.5 text-sm text-white border border-zinc-700 hover:border-[#B5FF00]/50 cursor-pointer transition-all`}>
                      #{tag.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Cultural Tags */}
              <div>
                <p className="font-mono text-xs text-zinc-500 tracking-widest mb-3">CULTURAL / FUSION</p>
                <div className="flex flex-wrap gap-2">
                  {tags.cultural.map((tag) => (
                    <span key={tag.name} className={`${tag.color} px-3 py-1.5 text-sm text-white border border-zinc-700 hover:border-[#B5FF00]/50 cursor-pointer transition-all`}>
                      #{tag.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Energy Tags */}
              <div>
                <p className="font-mono text-xs text-zinc-500 tracking-widest mb-3">ENERGY / BPM</p>
                <div className="flex flex-wrap gap-2">
                  {tags.energy.map((tag) => (
                    <span key={tag.name} className={`${tag.color} px-3 py-1.5 text-sm text-white border border-zinc-700 hover:border-[#B5FF00]/50 cursor-pointer transition-all`}>
                      #{tag.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate Links Section - LOCKED */}
      <section className="py-16 border-t border-zinc-800 relative" data-testid="affiliate-section">
        {/* Lock Overlay */}
        <div className="absolute inset-0 bg-[#050505]/80 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-[#B5FF00]" />
            </div>
            <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-2">COMING SOON</h3>
            <p className="text-zinc-500 font-mono text-sm">Partner links launching May 20th</p>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-8 opacity-30">
          <div className="flex items-center gap-3 mb-8">
            <LinkIcon className="text-[#B5FF00]" size={24} />
            <h2 className="font-['Josefin_Sans'] text-3xl font-semibold text-white tracking-tight" style={{ letterSpacing: '-0.059em' }}>
              PARTNER LINKS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#0A0A0A] border border-zinc-800 p-6 h-32" />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2025 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VenuesPage;
